import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import TicketDetailView from '../TicketDetailView';
import AppContext from 'context/Context';
import {
  TicketsFilterTabs,
  TicketCardItem,
  TicketsEmptyState,
  TicketsLoadingState,
  AgentFilterSelect,
  TicketsPagination
} from './componentes';
import { useTicketsList } from './hooks/useTicketsList';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const TicketsView = ({ onTicketSelect, selectedTicket, setSelectedTicket, onTicketDetailLoaded, onInitialReady }) => {
  const [internalSelectedTicket, setInternalSelectedTicket] = useState(null);
  const { config: { isDark } = {} } = useContext(AppContext);
  const [filterTab, setFilterTab] = useState('todos');
  const [agents, setAgents] = useState([]);
  const { token, nombreCompleto, idUsuario } = useToken();
  const [selectedAgentId, setSelectedAgentId] = useState(idUsuario ? String(idUsuario) : '');
  const hasInitializedAgentRef = useRef(Boolean(idUsuario));
  const { showSuccessToast, showErrorToast } = useToast();

  const currentTicket = selectedTicket !== undefined ? selectedTicket : internalSelectedTicket;
  const isViewingDetail = Boolean(currentTicket);

  // Sincronizar el idUsuario por defecto solo una vez si el token demoró en hidratarse
  useEffect(() => {
    if (idUsuario && !hasInitializedAgentRef.current) {
      setSelectedAgentId(String(idUsuario));
      hasInitializedAgentRef.current = true;
    }
  }, [idUsuario]);

  // Solo consultar la lista de tickets a la BD si NO estamos dentro del detalle de un ticket
  const {
    tickets,
    counts,
    isLoading,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalRegistros,
    totalPages,
    reloadTickets,
    markTicketAsRead
  } = useTicketsList(filterTab, selectedAgentId, !isViewingDetail, 20, onInitialReady);

  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const isFetchingAgentsRef = useRef(false);
  const agentsLoadedRef = useRef(false);

  // Cargar lista de agentes activos (para asignaciones y filtro en el encabezado)
  const fetchAgents = useCallback(async () => {
    if (!token || isFetchingAgentsRef.current || agentsLoadedRef.current) return;
    isFetchingAgentsRef.current = true;
    setIsLoadingAgents(true);
    try {
      const response = await fetch('/api/SupportEngine/GetAgentsList', {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.data || (Array.isArray(result) ? result : []);
        setAgents(Array.isArray(data) ? data : []);
        agentsLoadedRef.current = true;
      }
    } catch {
      // Fallback silencioso si el endpoint aún no está listo
    } finally {
      isFetchingAgentsRef.current = false;
      setIsLoadingAgents(false);
    }
  }, [token]);

  // Cargar agentes al montar el componente para tener disponibles las opciones del filtro
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleAssignTicket = async (ticket, agent, isReassign = false) => {
    try {
      const endpoint = (isReassign || ticket.idUsuarioAsignado)
        ? '/api/SupportEngine/ChangeTicketResponsible'
        : '/api/SupportEngine/AssignTicketAgent';

      const payload = (isReassign || ticket.idUsuarioAsignado)
        ? {
          idTicket: ticket.idTicket,
          idNuevoUsuarioAsignado: agent.idUsuario
        }
        : {
          idTicket: ticket.idTicket,
          idUsuarioAsignado: agent.idUsuario
        };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      });

      const agentName = agent.vNombreCompleto || agent.vNombre || `Agente #${agent.idUsuario}`;
      if (showSuccessToast) {
        showSuccessToast(
          (isReassign || ticket.idUsuarioAsignado) ? 'Responsable actualizado' : 'Asignación exitosa',
          `Ticket #${ticket.idTicket} asignado a ${agentName}`
        );
      }
      reloadTickets();
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error de asignación', err.message);
      }
    }
  };

  const handleSelectTicket = ticket => {
    if (!ticket) return;
    const ticketId = ticket.idTicket || ticket.id;
    if (markTicketAsRead) {
      markTicketAsRead(ticketId);
    }
    const normalizedTicket = {
      ...ticket,
      id: ticketId,
      idTicket: ticketId,
      bSinLeer: false
    };
    if (setSelectedTicket) {
      setSelectedTicket(normalizedTicket);
    } else {
      setInternalSelectedTicket(normalizedTicket);
    }
    if (onTicketSelect) {
      onTicketSelect(normalizedTicket);
    }
  };

  const handleBackToList = () => {
    const currentTicketId = currentTicket?.idTicket || currentTicket?.id;
    if (currentTicketId && markTicketAsRead) {
      markTicketAsRead(currentTicketId);
    }
    if (setSelectedTicket) {
      setSelectedTicket(null);
    } else {
      setInternalSelectedTicket(null);
    }
    if (onTicketSelect) {
      onTicketSelect(null);
    }
  };

  if (currentTicket) {
    return (
      <TicketDetailView
        ticket={currentTicket}
        onBack={handleBackToList}
        onTicketUpdated={reloadTickets}
        onTicketDetailLoaded={(data) => {
          if (onTicketDetailLoaded) onTicketDetailLoaded(data);
          if (onInitialReady) onInitialReady();
        }}
      />
    );
  }

  return (
    <div className="tickets-view-container">
      {/* Top Filter Tabs Bar with Agent Filter */}
      <div
        className="rounded-3 p-2 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2 shadow-none"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
        }}
      >
        <TicketsFilterTabs
          filterTab={filterTab}
          setFilterTab={setFilterTab}
          counts={counts}
        />
        <AgentFilterSelect
          selectedAgentId={selectedAgentId}
          onSelectAgent={setSelectedAgentId}
          agents={agents}
          currentUserId={idUsuario}
          currentUserName={nombreCompleto}
          isLoadingAgents={isLoadingAgents}
        />
      </div>

      {/* Ticket List Cards / Loader / Empty State / Pagination */}
      {isLoading && tickets.length === 0 ? (
        <TicketsLoadingState />
      ) : tickets.length === 0 ? (
        <TicketsEmptyState filterTab={filterTab} onReload={reloadTickets} />
      ) : (
        <>
          <div className="d-flex flex-column gap-3">
            {tickets.map(ticket => (
              <TicketCardItem
                key={ticket.idTicket}
                ticket={ticket}
                agents={agents}
                isLoadingAgents={isLoadingAgents}
                onRequestAgents={fetchAgents}
                onSelectTicket={handleSelectTicket}
                onAssignTicket={handleAssignTicket}
              />
            ))}
          </div>

          <TicketsPagination
            currentPage={page}
            totalPages={totalPages}
            totalRecords={totalRegistros}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size);
              setPage(1);
            }}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
};

export default TicketsView;
